import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { authFetch } from "../App";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ListTodo, Calendar, ChevronRight, Briefcase, User, Plus,
  Circle, Clock, CheckCircle2, AlertOctagon, Loader2,
} from "lucide-react";

const API = "/api";

const STATUS_ICON = {
  offen: Circle,
  in_arbeit: Clock,
  erledigt: CheckCircle2,
  blockiert: AlertOctagon,
};
const STATUS_COLOR = {
  offen: "text-gray-400",
  in_arbeit: "text-amber-500",
  erledigt: "text-emerald-500",
  blockiert: "text-red-500",
};
const PRIO_DOT = {
  niedrig: "bg-gray-400",
  normal: "bg-blue-500",
  hoch: "bg-amber-500",
  kritisch: "bg-red-500",
};

function dueLabel(iso) {
  if (!iso) return { text: "Ohne Datum", color: "#9CA3AF", bg: "#F3F4F6", border: "#E5E7EB" };
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  const target = new Date(y, m - 1, d); target.setHours(0, 0, 0, 0);
  const diff = Math.round((target - today) / 86400000);
  if (diff < 0) return { text: `${Math.abs(diff)}d ü.`, color: "#DC2626", bg: "#FEF2F2", border: "#FECACA" };
  if (diff === 0) return { text: "Heute", color: "#2563EB", bg: "#EFF6FF", border: "#BFDBFE" };
  if (diff === 1) return { text: "Morgen", color: "#16A34A", bg: "#F0FDF4", border: "#BBF7D0" };
  const days = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];
  return { text: `${days[target.getDay()]}, ${String(d).padStart(2, "0")}.${String(m).padStart(2, "0")}`, color: "#6B7280", bg: "#F9FAFB", border: "#E5E7EB" };
}

export default function MyTasksWidget() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [quickTitle, setQuickTitle] = useState("");
  const [creating, setCreating] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["my-tasks-widget"],
    queryFn: async () => {
      const res = await authFetch(`${API}/my-tasks?include_done=false&group=false`);
      if (!res.ok) return { tasks: [] };
      return res.json();
    },
    refetchInterval: 60000,
  });

  const handleQuickAdd = async (e) => {
    e.preventDefault();
    const title = quickTitle.trim();
    if (!title) return;
    setCreating(true);
    try {
      const res = await authFetch(`${API}/personal-tasks`, {
        method: "POST",
        body: JSON.stringify({ title, priority: "normal" }),
      });
      if (res.ok) {
        setQuickTitle("");
        toast.success("Aufgabe erstellt");
        queryClient.invalidateQueries({ queryKey: ["my-tasks-widget"] });
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Fehler beim Erstellen");
      }
    } catch { toast.error("Netzwerkfehler"); }
    setCreating(false);
  };

  if (isError) return null;
  const tasks = (data?.tasks || []).slice(0, 8);

  return (
    <div
      data-testid="my-tasks-widget"
      className="bg-white border border-gray-200 mb-3"
      style={{ borderRadius: 2 }}
    >
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <ListTodo className="w-3.5 h-3.5 text-slate-500" />
          <span className="text-[13px] font-semibold text-gray-800">Meine Aufgaben</span>
          {!isLoading && (
            <span className="text-[10px] text-gray-400 font-normal">
              {tasks.length === 0 ? "Keine" : (data?.total || 0)}
            </span>
          )}
        </div>
        <button
          onClick={() => navigate("/app/m/meine-aufgaben")}
          className="flex items-center gap-0.5 text-[11px] text-slate-500 hover:text-slate-800 transition-colors"
          data-testid="open-my-tasks-btn"
        >
          Alle anzeigen
          <ChevronRight className="w-3 h-3" />
        </button>
      </div>

      <div className="px-3 py-2">
        <form onSubmit={handleQuickAdd} className="flex items-center gap-2 mb-2 pb-2 border-b border-gray-100"
          data-testid="quick-add-form">
          <Plus className="w-3.5 h-3.5 text-gray-400 shrink-0" />
          <input
            type="text"
            value={quickTitle}
            onChange={e => setQuickTitle(e.target.value)}
            placeholder="Neue Aufgabe... (Enter zum Speichern)"
            disabled={creating}
            className="flex-1 bg-transparent outline-none text-[12px] text-gray-800 placeholder:text-gray-400"
            data-testid="quick-add-input"
          />
          {quickTitle.trim() && (
            <button type="submit" disabled={creating}
              className="px-2 py-0.5 rounded-sm bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-[10px] font-semibold transition-colors flex items-center gap-1"
              data-testid="quick-add-submit">
              {creating ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : "Speichern"}
            </button>
          )}
        </form>
        {isLoading ? (
          <div className="flex items-center justify-center py-4">
            <div className="w-4 h-4 border-2 border-gray-200 border-t-gray-500 rounded-full animate-spin" />
          </div>
        ) : tasks.length === 0 ? (
          <p className="text-[12px] text-gray-400 py-3 text-center" data-testid="no-tasks-msg">
            Keine offenen Aufgaben
          </p>
        ) : (
          <div className="space-y-1" data-testid="tasks-list">
            {tasks.map(t => {
              const d = dueLabel(t.due_date);
              const StatusIcon = STATUS_ICON[t.status] || Circle;
              const isPersonal = t.task_type === "PERSONAL";
              return (
                <div
                  key={t.id}
                  className="group flex items-start gap-2.5 py-1.5 px-2 rounded-sm hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => {
                    if (t.project_id) navigate(`/app/projekte/${t.project_id}/aufgaben`);
                    else navigate("/app/m/meine-aufgaben");
                  }}
                  data-testid={`task-item-${t.id}`}
                >
                  <div
                    className="shrink-0 flex flex-col items-center justify-center w-11 py-1 rounded-sm text-center"
                    style={{ backgroundColor: d.bg, border: `1px solid ${d.border}` }}
                  >
                    <span className="text-[10px] font-semibold leading-none" style={{ color: d.color }}>
                      {d.text}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${PRIO_DOT[t.priority] || PRIO_DOT.normal}`} />
                      <StatusIcon className={`w-3 h-3 shrink-0 ${STATUS_COLOR[t.status] || "text-gray-400"}`} />
                      <span className="text-[12px] font-medium text-gray-800 truncate">{t.title}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      {isPersonal ? (
                        <span className="flex items-center gap-0.5 text-[11px] text-indigo-500">
                          <User className="w-2.5 h-2.5" /> Privat
                        </span>
                      ) : (
                        <span className="flex items-center gap-0.5 text-[11px] text-blue-500 truncate">
                          <Briefcase className="w-2.5 h-2.5" />
                          <span className="truncate">{t.project_name || "Projekt"}</span>
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
